import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import RoleAssignments, { DemoMember } from './RoleAssignments'
import { classifyRoleError } from './roleErrors'
import { roleLabel } from './roleKeys'
import { useAuthStore } from '@/stores/authStore'

type Vars = { role: string; userId: string }

export default function SharedRoleAssignments({organizationId,members,loading,error}: {organizationId:string;members:DemoMember[];loading:boolean;error:boolean}) {
 const userId=useAuthStore(s=>s.user?.id)
 // Scoped to organization and signed-in identity; App clears the cache when the identity ends.
 const key=['clinic-workflow-roles',organizationId,userId]
 const client=useQueryClient()
 const [message,setMessage]=useState('')
 const query=useQuery({queryKey:key,queryFn:()=>api.getWorkflowRoles(organizationId),refetchInterval:30000})
 const mutation=useMutation({
  mutationFn:({role,userId}:Vars)=>api.setWorkflowRole(organizationId,role,{user_id:userId||null,expected_revision:query.data?.find(item=>item.role_key===role)?.revision||0}),
  onSuccess:(data,{role})=>{client.setQueryData(key,data);setMessage(`Saved ${roleLabel(role)} for the team.`)},
  onError:(failure,{role})=>{
   const {kind,message:text}=classifyRoleError(failure)
   setMessage(`${roleLabel(role)}: ${text}`)
   // A refusal means our copy may be stale: show the server's current values.
   if(kind!=='service') client.invalidateQueries({queryKey:key})
  },
 })
 const assignments=query.data||[]
 const loadFailed=query.isError&&!query.data
 const status=mutation.isPending?'Saving…':loadFailed?'Assignments could not be loaded. Reload the page to retry.':message
 return <RoleAssignments
  scope={organizationId}
  members={members}
  loading={loading||query.isPending}
  error={error||loadFailed}
  sharedAssignments={Object.fromEntries(assignments.map(item=>[item.role_key,item.user_id||'']))}
  staleRoles={assignments.filter(item=>item.assignee_active===false).map(item=>item.role_key)}
  saving={mutation.isPending}
  saveMessage={status}
  onAssign={(role,userId)=>{setMessage('');mutation.mutate({role,userId})}}
 />
}
